import { useMemo, useState } from 'react';
import { Check, RotateCcw, Trophy, XCircle } from 'lucide-react';
import type { CrmActivity, CrmDealDetail } from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { formatMoney } from '@/utils/currency';
import { daysInStage, daysTone, fmtDate, isClosedDeal, type StageIndex } from './dealMath';
import { TONE_BG, TONE_BORDER, TONE_TEXT } from './tones';
import type { MoveDealFn } from './useDealBoard';

interface Props {
  deal: CrmDealDetail;
  stageIndex: StageIndex;
  activities: ReadonlyArray<CrmActivity>;
  now: Date;
  pending: boolean;
  onMove: MoveDealFn;
}

/**
 * Connected stage pills for an open deal (every pill is a real button, so
 * a stage change is never drag-only) or the won/lost banner with a
 * "return to pipeline" confirmation for a closed one.
 */
export function StageStepper({ deal, stageIndex, activities, now, pending, onMove }: Props) {
  const [resumeOpen, setResumeOpen] = useState(false);
  const { open, won, lost } = stageIndex;
  const stage = stageIndex.byId.get(deal.stageId);
  const closed = isClosedDeal(deal, stage);

  const lastStageChange = useMemo(
    () =>
      [...activities]
        .filter((activity) => activity.type === 'stage_change')
        .sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1))[0] ?? null,
    [activities],
  );

  if (closed) {
    const isWon = stage?.isWon ?? (deal.closedAt !== null && !deal.lostReason);
    const fromKey = lastStageChange?.payload?.fromStageKey as string | undefined;
    const previous = open.find((item) => item.key === fromKey) ?? open[0];
    const closedDate = deal.closedAt ? fmtDate(deal.closedAt) : '—';
    const tone = isWon ? 'emerald' : 'destructive';
    return (
      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm',
          TONE_BORDER[tone],
          TONE_BG[tone],
          TONE_TEXT[tone],
        )}
      >
        {isWon ? <Trophy className="size-4 shrink-0" /> : <XCircle className="size-4 shrink-0" />}
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="font-medium">
            {isWon
              ? t('crm.deals.stepper.wonBanner', {
                  date: closedDate,
                  value: deal.valueNet === null ? '—' : formatMoney(deal.valueNet, deal.currency),
                })
              : t('crm.deals.stepper.lostBanner', { date: closedDate })}
          </div>
          {isWon ? (
            <div className="text-xs opacity-80">{t('crm.deals.stepper.wonCustomer')}</div>
          ) : (
            <div className="text-xs">
              {deal.lostReason ? (
                <span className="font-medium">{t('crm.deals.stepper.reason', { reason: deal.lostReason })}</span>
              ) : null}
              {lastStageChange?.authorDisplayName ? (
                <span className="opacity-80">
                  {' · '}
                  {t('crm.deals.stepper.enteredBy', { author: lastStageChange.authorDisplayName })}
                </span>
              ) : null}
            </div>
          )}
        </div>
        {previous ? (
          <>
            <Button variant="outline" size="sm" disabled={pending} onClick={() => setResumeOpen(true)}>
              <RotateCcw className="size-3.5" />
              {t('crm.deals.stepper.resume')}
            </Button>
            <AlertDialog open={resumeOpen} onOpenChange={setResumeOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('crm.deals.stepper.resume')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('crm.deals.stepper.resumeConfirm', { stage: previous.name })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      setResumeOpen(false);
                      void onMove(deal.id, previous.id);
                    }}
                  >
                    {t('common.confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : null}
      </div>
    );
  }

  const currentIndex = open.findIndex((item) => item.id === deal.stageId);
  const days = daysInStage(deal, now);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center overflow-x-auto py-1">
          {open.map((item, index) => {
            const state = index < currentIndex ? 'passed' : index === currentIndex ? 'current' : 'future';
            return (
              <div key={item.id} className="flex shrink-0 items-center">
                {index > 0 ? <span className="bg-border h-px w-3" /> : null}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      disabled={pending || state === 'current'}
                      onClick={() => void onMove(deal.id, item.id)}
                      className={cn(
                        'flex h-7 items-center gap-1 rounded-full px-3 text-xs whitespace-nowrap transition-colors motion-reduce:transition-none',
                        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none disabled:cursor-default',
                        state === 'passed' && 'bg-primary/15 text-primary hover:bg-primary/25',
                        state === 'current' && 'bg-primary text-primary-foreground ring-primary/40 ring-2',
                        state === 'future' && 'bg-muted text-muted-foreground hover:bg-muted/70',
                      )}
                    >
                      {state === 'passed' ? <Check className="size-3" /> : null}
                      {item.name}
                      {state === 'current' ? <span className="opacity-80">· {deal.probability ?? item.probability} %</span> : null}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('crm.deals.stepper.moveTo', { stage: item.name, p: item.probability })}</TooltipContent>
                </Tooltip>
              </div>
            );
          })}
        </div>
        <div className="flex shrink-0 gap-1">
          {won ? (
            <Button
              variant="outline"
              size="sm"
              disabled={pending}
              className={TONE_TEXT.emerald}
              onClick={() => void onMove(deal.id, won.id)}
            >
              <Trophy className="size-3.5" />
              {t('crm.deals.stepper.won')}
            </Button>
          ) : null}
          {lost ? (
            <Button
              variant="outline"
              size="sm"
              disabled={pending}
              className={TONE_TEXT.destructive}
              onClick={() => void onMove(deal.id, lost.id)}
            >
              <XCircle className="size-3.5" />
              {t('crm.deals.stepper.lost')}
            </Button>
          ) : null}
        </div>
      </div>
      <p className={cn('text-xs', TONE_TEXT[daysTone(days)])}>
        {t(days === 1 ? 'crm.deals.stepper.inStageSinceOne' : 'crm.deals.stepper.inStageSince', {
          stage: deal.stageName,
          date: fmtDate(deal.stageEnteredAt),
          days,
          p: deal.probability ?? stage?.probability ?? 0,
        })}
      </p>
    </div>
  );
}
